import type { CaseWebsiteInfo } from "./central-case-website";

import { getFunctions, httpsCallable } from "firebase/functions";

import { firebaseApp } from "./firebase";
import { publishCentralCaseChange } from "./central-case-events";

export type CentralCaseStatus =
  | "first_call"
  | "arrangement_review"
  | "paperwork"
  | "service_scheduled"
  | "completed"
  | "archived";

export type CentralLifecycleStatus =
  | "case_created"
  | "arrangement_in_progress"
  | "contract_pending"
  | "payment_pending"
  | "active"
  | "services_in_progress"
  | "services_completed"
  | "follow_up_required"
  | "ready_to_close"
  | "complete"
  | "archived"
  | "reopened"
  | "cancelled";

export type CentralLifecycle = {
  archivedAt: string | null;
  completionSnapshotVersion: number;
  reopenedAt: string | null;
  status: CentralLifecycleStatus;
  updatedAt: string | null;
  version: number;
};

export type CentralLifecycleClosure = {
  blockers: Array<{ gate: string; message: string }>;
  caseId: string;
  evaluatedAt: string;
  exceptionIds: string[];
  ready: boolean;
};

export type CentralCaseIntakeMode = "immediate_need" | "pre_need" | "manual";
export type CentralCaseSource = "phone" | "walk_in" | "transfer" | "family_link" | "staff_intake";

export type CentralCaseRecord = {
  websiteInfo?: CaseWebsiteInfo;
  closedAt?: string | null;
  balanceDueCents?: number | null;
  id: string;
  contactEmail?: string;
  createdAt: string | null;
  documentStatus?: "complete" | "missing" | "not_started";
  familySubmissionRevision?: number;
  familySubmissionVersionId?: string;
  disposition: "burial" | "cremation" | "undecided";
  funeralHomeId: string;
  locationId?: string | null;
  locationName?: string | null;
  intakeMode?: CentralCaseIntakeMode;
  lovedOneName: string;
  missingDocuments?: number;
  ownerName: string;
  placeholderCase?: boolean;
  paymentStatus?: "balance_due" | "paid" | "restricted" | "not_started";
  phone: string;
  primaryContact: string;
  primaryContactRelationship?: string;
  serviceDate?: string | null;
  signatureCount?: number;
  signatureStatus?: "complete" | "pending" | "not_started";
  source?: string;
  status: CentralCaseStatus;
  updatedAt: string | null;
};

export type CentralCaseCreateInput = Pick<
  CentralCaseRecord,
  "disposition" | "lovedOneName" | "phone" | "primaryContact"
> & {
  contactEmail?: string;
  intakeMode?: CentralCaseIntakeMode;
  locationId?: string | null;
  ownerName?: string;
  placeholderCase?: boolean;
  source?: CentralCaseSource;
};

export type CentralCaseUpdateInput = Partial<
  Pick<
    CentralCaseRecord,
    "disposition" | "lovedOneName" | "ownerName" | "phone" | "primaryContact" | "status" | "locationId"
  >
>;
export type CentralCaseCloseReadiness = { blockers: string[]; caseId: string; ready: boolean };

const functions = getFunctions(firebaseApp, "us-east1");

export async function listCentralCases() {
  const callable = httpsCallable<void, { cases: CentralCaseRecord[] }>(functions, "listCentralCases");
  return (await callable()).data.cases;
}

export async function createCentralCase(input: CentralCaseCreateInput) {
  const callable = httpsCallable<CentralCaseCreateInput, { case: CentralCaseRecord }>(functions, "createCentralCase");
  return (await callable(input)).data.case;
}

export async function updateCentralCase(caseId: string, updates: CentralCaseUpdateInput) {
  const callable = httpsCallable<
    { caseId: string; updates: CentralCaseUpdateInput },
    { case: CentralCaseRecord }
  >(functions, "updateCentralCase");
  const updated = (await callable({ caseId, updates })).data.case;
  publishCentralCaseChange(updated);
  return updated;
}
export async function getCentralCaseCloseReadiness(caseId: string, balanceResolution?: "paid" | "waived" | "not_applicable") { const callable = httpsCallable<{ balanceResolution?: string; caseId: string }, CentralCaseCloseReadiness>(functions, "getCentralCaseCloseReadiness"); return (await callable({ balanceResolution, caseId })).data; }
export async function closeCentralCase(input: { balanceResolution: "paid" | "waived" | "not_applicable"; caseId: string; resolutionNote: string }) { const callable = httpsCallable<typeof input, { caseId: string; status: "completed" }>(functions, "closeCentralCase"); return (await callable(input)).data; }
export async function archiveCentralCase(caseId: string) { const callable = httpsCallable<{ caseId: string }, { caseId: string; status: "archived" }>(functions, "archiveCentralCase"); return (await callable({ caseId })).data; }

export async function getCentralCaseLifecycle(caseId: string) {
  const callable = httpsCallable<{ caseId: string }, { lifecycle: CentralLifecycle }>(functions, "getCentralCaseLifecycle");
  return (await callable({ caseId })).data.lifecycle;
}

export async function evaluateCentralCaseClosure(caseId: string) {
  const callable = httpsCallable<{ caseId: string }, CentralLifecycleClosure>(functions, "evaluateCentralCaseClosure");
  return (await callable({ caseId })).data;
}

export async function transitionCentralCaseLifecycle(input: {
  caseId: string;
  correlationId?: string;
  expectedVersion: number;
  idempotencyKey: string;
  reason: string;
  reopen?: { dueAt?: string | null; owner: string; scope: string };
  target: CentralLifecycleStatus;
}) {
  const callable = httpsCallable<typeof input, { lifecycleVersion: number; status: CentralLifecycleStatus }>(functions, "transitionCentralCaseLifecycle");
  return (await callable(input)).data;
}

export async function bulkUpdateCentralCases(caseIds: string[], updates: CentralCaseUpdateInput) {
  const callable = httpsCallable<{ caseIds: string[]; updates: CentralCaseUpdateInput }, { updatedCaseIds: string[] }>(functions, "bulkUpdateCentralCases");
  return (await callable({ caseIds, updates })).data.updatedCaseIds;
}

export async function deleteCentralCase(caseId: string) {
  const callable = httpsCallable<{ caseId: string }, { caseId: string }>(functions, "deleteCentralCase");
  return (await callable({ caseId })).data.caseId;
}

export async function clearCentralDemoCases(confirmation: "CLEAR CASES") {
  const callable = httpsCallable<
    { confirmation: "CLEAR CASES" },
    { clearedCaseCount: number }
  >(functions, "clearCentralDemoCases");
  return (await callable({ confirmation })).data;
}

export function getCentralCaseErrorMessage(error: unknown) {
  const code = typeof error === "object" && error && "code" in error ? String(error.code) : "";
  if (code === "functions/permission-denied" || code === "functions/unauthenticated") {
    return "Your Central session cannot complete this case operation. Sign in again or contact an administrator.";
  }
  if (code === "functions/invalid-argument") {
    return "Check the required case details and try again.";
  }
  if (code === "functions/not-found") {
    return "This case no longer exists. Refresh the list.";
  }
  if (code === "functions/already-exists") {
    return "A similar case may already exist. Review the case list before creating another one.";
  }
  return "Central couldn’t save this case. Try again.";
}
